import type { OrderType } from '@scripsteam/scrips-signal-ds';
import type { ProposalSource } from './ambient-types';
import { serverMatchOf } from './matched-code';

/**
 * Adapts an ambient `ProposalSource` into the shape the A/P per-type dialogs expect (PROD-1975).
 *
 * WHY THIS EXISTS — a crash on dev, not a tidiness exercise.
 *
 * PROD-1972 handed `decision.seed` (a raw `ProposalSource`) straight to `pendingSeed.item`. That
 * field is typed `unknown`, so nothing objected at compile time, and the two shapes disagree in a
 * way that only surfaces at runtime:
 *
 *     AmbientMedication.route : string            e.g. "PO"
 *     MedicationPick.route    : { display, code }[]
 *
 * `toMedicationPick` copies `route` through verbatim, and `medication-dose-dialog` then does
 * `(medication.route ?? []).map(...)`. Against a string that throws
 * `(e.route ?? []).map is not a function` and takes the whole encounter page down with a router
 * error boundary.
 *
 * It escaped the browser test because the sample proposal had NO route — `(undefined ?? []).map`
 * is perfectly happy. The bug needs the model to actually emit one, so a passing manual test
 * proved nothing about the field that breaks it. Hence the unit tests beside this file, which
 * use a proposal WITH a route.
 *
 * WHAT IS DELIBERATELY DROPPED
 *
 * The model's `dose`, `route`, `frequency` and `duration` are free text ("PO", "BD", "5 days").
 * The dialogs want coded values chosen from Master's lists. Translating one into the other needs
 * a terminology lookup that does not exist here, and guessing would put an unverified route on a
 * prescription — worse than asking. So the seed carries the NAME only, and the clinician supplies
 * the rest in the dialog. That is also what `accept-rules.ts` already assumes when it sends every
 * medication and vaccination to a dialog rather than adding them directly: "a tick cannot supply
 * dose, route or frequency".
 *
 * The utterance stays visible on the proposal card, so what was actually said is not lost — it is
 * just not silently converted into structured prescribing data.
 */
export function toOrderSeed(kind: OrderType, source: ProposalSource): unknown {
  // Identity lives under a different key per kind: `name` for labs, medications, vaccinations and
  // procedures, `modality` for radiology. Asserting `name` on all of them is a mistake I have already
  // made once in a test fixture.
  const name =
    ('name' in source && source.name) ||
    ('modality' in source && source.modality) ||
    '';

  /**
   * PROD-3184: THE SERVER'S MATCH, when there is one.
   *
   * Master looked the item up with the app's own search for this kind and found exactly one record.
   * That record is what the dialog opens with, chosen: its code, its system and its catalogue name.
   * Still a suggestion: the clinician saves the dialog, or closes it and nothing is added.
   *
   * Never for a medication: the dose dialog needs the catalogue row's route, unit and strength, which
   * a code does not carry, so medications go to the search first (`accept-rules.ts`) and never reach
   * here with a match that matters.
   */
  const match = kind === 'medication' ? undefined : serverMatchOf(source);
  if (match) {
    const display = match.display || name || match.code;
    // A vaccine opens from a bare coding: `VaccineAddDialog` looks the code up in the DDC catalogue
    // itself (route and unit of measurement included), as it does for a favourite.
    if (kind === 'vaccination') {
      return { code: match.code, display, ...(match.system ? { system: match.system } : {}) };
    }
    // Lab / imaging / procedure: a `CodeSearchResult`, keyed on the code so a second suggestion
    // opened over this one remounts the dialog. Radiology has no system ("" on the wire): none is set.
    return { id: match.code, code: match.code, display, ...(match.system ? { system: match.system } : {}) };
  }

  if (!name) return undefined;

  // Medication and vaccination go through `toMedicationPick`, which reads
  // `name ?? display ?? displayName`. Nothing else from the ambient source is shape-compatible,
  // so nothing else is passed.
  if (kind === 'medication' || kind === 'vaccination') {
    return { name };
  }

  // PROD-3184: an UNMATCHED ambient procedure is not a pick. An uncoded procedure is not an order,
  // so the dialog opens on its search, filled in with the dictated name (`ambientSearchText`).
  // Marked as ambient so a dragged item is never read this way.
  if (kind === 'procedure') {
    return { display: name, [AMBIENT_SEARCH_SEED]: true };
  }

  // Coded orders (lab / imaging / referral) preselect from a `CodeSearchResult`-shaped item, which
  // is keyed on `display`. No `code`: the server matched none (or was not asked), and a fabricated
  // code would be worse than an unmatched label the clinician resolves in the picker.
  return { display: name };
}

/**
 * PROD-3184: the mark on an ambient seed that carries SEARCH TEXT, not a record. Only `toOrderSeed`
 * sets it, so a drag or a rail pick (which never carries it) keeps opening as it always did.
 */
export const AMBIENT_SEARCH_SEED = 'ambientSearchSeed';

/** The search text an ambient seed carries, or `undefined` for any other seed (a drag, a pick, a match). */
export function ambientSearchText(seed: unknown): string | undefined {
  if (!seed || typeof seed !== 'object') return undefined;
  const s = seed as { display?: unknown; [AMBIENT_SEARCH_SEED]?: unknown };
  if (s[AMBIENT_SEARCH_SEED] !== true || typeof s.display !== 'string') return undefined;
  return s.display.trim() || undefined;
}
